import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { hasPermission, getLessonTypeConfig } from '../config/lesson-types.config';
import { LessonType, Role } from '../core/types';
import { initializePluginSystem, registerAllPlugins } from '../config/slide-plugins.config';
import { slideRegistry } from '../core/plugin-system';

/** Tipurile pe care le inregistreaza `registerAllPlugins()`. La a doua vizita sunt deja acolo. */
const TIPURI_PLUGIN = ['QUESTION', 'IMAGE_UPLOAD', 'TEXT', 'VIDEO_PREVIEW', 'INFO'];
const pluginuriInregistrate = () => TIPURI_PLUGIN.every((tip) => slideRegistry.has(tip));

// New system components (always used - migration complete)
import { LessonListingPage } from '../features/listing';
import { LessonCreator } from '../features/creation';
import { LessonPlayer } from '../features/completion';

// Auth provider for role checking
import { useAuthProviderV2 } from '../../../providers/provider-hooks';

interface LessonSystemRouterProps {
  action: 'list' | 'create' | 'complete';
  lessonType: LessonType;
}

/**
 * Unified router component for the new lesson system.
 * Migration complete - all feature flags removed.
 *
 * Features:
 * - Permission checking
 * - Support for all 4 lesson types
 * - Supports list, create, and complete actions
 *
 * @example
 * // In routes-provider.jsx
 * <LessonSystemRouter action="list" lessonType={LessonType.LESSON} />
 * <LessonSystemRouter action="create" lessonType={LessonType.HOMEWORK} />
 * <LessonSystemRouter action="complete" lessonType={LessonType.PRACTICAL_ACTIVITY} />
 */
export const LessonSystemRouter: React.FC<LessonSystemRouterProps> = ({
  action,
  lessonType,
}) => {
  const { id } = useParams<{ id?: string }>();
  const { userInfo } = useAuthProviderV2();
  const userRole = userInfo?.role as Role;

  // ============================================================================
  // LAZY PLUGIN INITIALIZATION
  // ============================================================================
  // Initialize slide plugin system on first lesson page visit
  // (plugins are only needed for lesson pages, not for other parts of the app)
  //
  // 🔴 `pluginsGata` e STARE, nu doar un efect: `registerAllPlugins()` face cinci `import()`
  // (444-790 ms masurat), iar registrul nu anunta pe nimeni cand termina. Fara o re-randare,
  // playerul ramanea pe „Slide invalid" — vizibil in previzualizare, care porneste singura
  // (masurat 2026-10-22 pe stub); in modul normal cursa era mascata de butonul „Începe lecția".
  const [pluginsGata, setPluginsGata] = useState(pluginuriInregistrate);
  useEffect(() => {
    let activ = true;

    const initPlugins = async () => {
      try {
        initializePluginSystem();
        await registerAllPlugins();
        console.log('✅ Lesson plugin system initialized successfully');
        if (activ) setPluginsGata(true);
      } catch (error) {
        console.error('❌ Failed to initialize lesson plugin system:', error);
      }
    };

    initPlugins();
    return () => {
      activ = false;
    };
  }, []);

  // Get lesson type configuration
  const config = getLessonTypeConfig(lessonType);

  // ============================================================================
  // PERMISSION CHECKS
  // ============================================================================

  // Check if this is a preview route (preview only requires 'view' permission)
  const isPreview = window.location.pathname.includes('/preview');

  // Check if user has permission for the requested action
  const permissionMap: Record<typeof action, keyof typeof config.permissions> = {
    list: 'view',
    create: id ? 'edit' : 'create',
    complete: isPreview ? 'view' : 'complete', // Preview uses 'view' permission
  };

  const requiredPermission = permissionMap[action];
  const hasRequiredPermission = hasPermission(userRole, lessonType, requiredPermission);

  if (!hasRequiredPermission) {
    return (
      <div className="flex items-center justify-center min-h-dvh bg-muted">
        <div className="text-center p-8 bg-card rounded-lg shadow-lg max-w-md">
          <div className="text-6xl mb-4">🔒</div>
          <h2 className="text-2xl font-bold text-foreground mb-2">Acces temporar restricționat</h2>
          <p className="text-muted-foreground mb-4">
            Nu am deschis încă această secțiune.
          </p>
          <p className="text-sm text-muted-foreground">.
          </p>
        </div>
      </div>
    );
  }

  // ============================================================================
  // ROUTE NEW SYSTEM COMPONENTS (migration complete)
  // ============================================================================

  switch (action) {
    case 'list':
      return <LessonListingPage lessonType={lessonType} userRole={userRole} />;

    case 'create':
      return <LessonCreator lessonType={lessonType} lessonId={id} />;

    case 'complete':
      // ✅ FIX: Ensure userId is available before rendering LessonPlayer
      // Backend requires userId for answer submissions
      // NOTE: JWT token uses 'userId' field, not 'id'
      if (!isPreview && !userInfo?.userId) {
        return (
          <div className="flex items-center justify-center min-h-dvh bg-muted">
            <div className="text-center p-8 bg-card rounded-lg shadow-lg max-w-md">
              <div className="text-6xl mb-4">⚠️</div>
              <h2 className="text-2xl font-bold text-foreground mb-2">Sesiune expirată</h2>
              <p className="text-muted-foreground mb-4">
                Te rugăm să te autentifici din nou pentru a continua lecția.
              </p>
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
              >
                Reîncarcă pagina
              </button>
            </div>
          </div>
        );
      }

      return (
        <LessonPlayer
          lessonId={id!}
          lessonType={lessonType}
          userId={userInfo?.userId}
          isPreview={isPreview}
          pluginsReady={pluginsGata}
        />
      );

    default:
      return (
        <div className="flex items-center justify-center min-h-dvh bg-muted">
          <div className="text-center">
            <h2 className="text-xl font-bold text-foreground">Unknown action: {action}</h2>
          </div>
        </div>
      );
  }
};

export default LessonSystemRouter;
